import { z } from 'zod';
import { baseEntityFields, idSchema, slugSchema, hostnameSchema, } from './common.schema';
/**
 * Dashboards (drag-and-drop, per-user + shared, JSON import/export) and
 * white-label public status pages (PRD §2.7 — both P0).
 */
export const WIDGET_TYPES = [
    'line_graph',
    'area_graph',
    'big_number',
    'gauge',
    'table',
    'alert_list',
    'noc_status',
    'sla',
    'website_status',
    'text',
];
export const widgetPositionSchema = z
    .object({
    x: z.number().int().min(0).max(47),
    y: z.number().int().min(0).max(1000),
    w: z.number().int().min(1).max(48),
    h: z.number().int().min(1).max(100),
})
    .strict()
    .superRefine((val, ctx) => {
    // 48-column grid: a widget must fit inside it.
    if (val.x + val.w > 48) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['w'],
            message: 'widget exceeds the 48-column grid (x + w must be <= 48)',
        });
    }
});
export const widgetSchema = z
    .object({
    id: z.string().min(1).max(64),
    type: z.enum(WIDGET_TYPES),
    title: z.string().min(1).max(200),
    position: widgetPositionSchema,
    /**
     * Widget-type-specific configuration (datapoint selectors, resource /
     * group filters, time range, thresholds for gauge coloring, text
     * content…). Validated in depth by the widget-data endpoint for the
     * declared type.
     */
    config: z.record(z.unknown()),
})
    .strict();
/* ------------------------------------------------------------------ *
 * Widget config contracts — validated in depth by the widget-data
 * endpoint for the declared type (the widget doc itself stores config
 * as an opaque record so dashboards never break on contract additions;
 * the DATA path is strict).
 * ------------------------------------------------------------------ */
/** One time-series selector (a datapoint on a resource/instance). */
export const metricSeriesSchema = z
    .object({
    resourceId: idSchema,
    sensorKey: z.string().min(1).max(100),
    instanceId: idSchema.nullable(),
    datapoint: z.string().min(1).max(100),
    /** Display label override. */
    label: z.string().min(1).max(200).optional(),
})
    .strict();
export const graphWidgetConfigSchema = z
    .object({
    series: z.array(metricSeriesSchema).min(1).max(10),
    /** Downsample bucket; null = server picks from the range. */
    bucketSeconds: z.number().int().min(60).max(86400).nullable().default(null),
})
    .strict();
export const bigNumberWidgetConfigSchema = z
    .object({
    series: metricSeriesSchema,
    aggregation: z.enum(['latest', 'avg', 'min', 'max']).default('latest'),
    unit: z.string().min(1).max(20).optional(),
})
    .strict();
export const gaugeWidgetConfigSchema = z
    .object({
    series: metricSeriesSchema,
    aggregation: z.enum(['latest', 'avg', 'min', 'max']).default('latest'),
    min: z.number().finite(),
    max: z.number().finite(),
    /** Coloring thresholds (display-only; alerting owns real thresholds). */
    warnAt: z.number().finite().nullable().default(null),
    critAt: z.number().finite().nullable().default(null),
    unit: z.string().min(1).max(20).optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.min >= val.max) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['max'],
            message: 'gauge max must exceed min',
        });
    }
});
export const tableWidgetConfigSchema = z
    .object({
    series: z.array(metricSeriesSchema).min(1).max(25),
    aggregation: z.enum(['latest', 'avg', 'min', 'max']).default('latest'),
})
    .strict();
export const alertListWidgetConfigSchema = z
    .object({
    clientId: idSchema.optional(),
    resourceIds: z.array(idSchema).min(1).max(10).optional(),
    minSeverity: z.enum(['warning', 'error', 'critical']).default('warning'),
    statuses: z
        .array(z.enum(['open', 'acknowledged']))
        .min(1)
        .default(['open', 'acknowledged']),
    limit: z.number().int().min(1).max(100).default(25),
})
    .strict();
export const nocStatusWidgetConfigSchema = z
    .object({
    /** Selectors are OR-combined narrows; none = whole tenant (bounded
     *  server-side). */
    resourceIds: z.array(idSchema).min(1).max(30).optional(),
    clientId: idSchema.optional(),
    group: z.string().min(1).max(100).optional(),
})
    .strict();
export const slaWidgetConfigSchema = z
    .object({
    resourceIds: z.array(idSchema).min(1).max(30),
    targetPct: z.number().min(0).max(100).default(99.9),
    windowDays: z.union([z.literal(7), z.literal(30), z.literal(90)]).default(30),
})
    .strict();
export const websiteStatusWidgetConfigSchema = z
    .object({
    resourceIds: z.array(idSchema).min(1).max(30),
})
    .strict();
export const textWidgetConfigSchema = z
    .object({
    /** Rendered client-side with sanitization; the server never
     *  interprets it. */
    markdown: z.string().min(1).max(10000),
})
    .strict();
/** Per-type config schema map — the widget-data endpoint dispatches on
 *  this; adding a widget type REQUIRES adding its contract here
 *  (exhaustiveness enforced by the Record type). */
export const widgetConfigSchemaByType = {
    line_graph: graphWidgetConfigSchema,
    area_graph: graphWidgetConfigSchema,
    big_number: bigNumberWidgetConfigSchema,
    gauge: gaugeWidgetConfigSchema,
    table: tableWidgetConfigSchema,
    alert_list: alertListWidgetConfigSchema,
    noc_status: nocStatusWidgetConfigSchema,
    sla: slaWidgetConfigSchema,
    website_status: websiteStatusWidgetConfigSchema,
    text: textWidgetConfigSchema,
};
/** Widget-data request: type + config validated per type, plus a time
 *  range consumed by time-series widgets (ignored by list/status
 *  widgets, which are always "now"). */
export const widgetDataRequestSchema = z
    .object({
    type: z.enum(WIDGET_TYPES),
    config: z.record(z.unknown()),
    startMs: z.number().int().nonnegative().optional(),
    endMs: z.number().int().nonnegative().optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    if ((val.startMs === undefined) !== (val.endMs === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endMs'],
            message: 'startMs and endMs must be provided together',
        });
    }
    if (val.startMs !== undefined &&
        val.endMs !== undefined &&
        val.startMs >= val.endMs) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endMs'],
            message: 'endMs must be after startMs',
        });
    }
});
export const dashboardInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    /** Shared dashboards are visible tenant-wide; private ones only to
     *  their owner. */
    shared: z.boolean().default(false),
    clientId: idSchema.optional(),
    widgets: z.array(widgetSchema).max(100).default([]),
})
    .strict()
    .superRefine((val, ctx) => widgetIdUniquenessRule(val, ctx));
/** Widget-id uniqueness, enforced on inputs AND stored docs. */
export function widgetIdUniquenessRule(val, ctx) {
    const ids = new Set(val.widgets.map((w) => w.id));
    if (ids.size !== val.widgets.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['widgets'],
            message: 'widget ids must be unique within a dashboard',
        });
    }
}
export const dashboardSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable(),
    shared: z.boolean(),
    /** Owner (creator) — authorization anchor for private dashboards. */
    ownerUserId: idSchema,
    clientId: idSchema.nullable(),
    widgets: z.array(widgetSchema).max(100),
})
    .extend(baseEntityFields)
    .strict()
    // Stored docs enforce the same invariants as inputs (fail-closed).
    .superRefine((val, ctx) => widgetIdUniquenessRule(val, ctx));
/* ------------------------------------------------------------------ *
 * Status pages
 * ------------------------------------------------------------------ */
export const STATUS_PAGE_COMPONENT_KINDS = ['resource', 'website'];
export const statusPageComponentSchema = z
    .object({
    kind: z.enum(STATUS_PAGE_COMPONENT_KINDS),
    resourceId: idSchema,
    displayName: z.string().min(1).max(200),
    /** Display order on the page. */
    order: z.number().int().min(0).max(1000),
})
    .strict();
/** Components with canonical identity/order: no duplicate resources, no
 *  colliding display orders — ambiguity here becomes UI flakiness. */
const componentsArraySchema = z
    .array(statusPageComponentSchema)
    .min(1)
    .max(200)
    .superRefine((components, ctx) => {
    const resourceIds = new Set(components.map((c) => c.resourceId));
    if (resourceIds.size !== components.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'components must not repeat a resource',
        });
    }
    const orders = new Set(components.map((c) => c.order));
    if (orders.size !== components.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'component display orders must be unique',
        });
    }
});
/** State machine per the Edge contract (edge-architecture.md §3):
 *  pending → verifying → dns_verified_cert_pending → active/failed. */
export const CUSTOM_DOMAIN_STATUSES = [
    'pending',
    'verifying',
    'dns_verified_cert_pending',
    'active',
    'failed',
];
/** Custom-domain state for the shared Meshtek Edge service
 *  (edge-architecture.md); Detect stores desired state + Edge sync status. */
export const statusPageCustomDomainSchema = z
    .object({
    domain: hostnameSchema,
    status: z.enum(CUSTOM_DOMAIN_STATUSES),
    /** Edge-side reference for the domain claim. */
    edgeClaimId: z.string().min(1).max(256).nullable(),
    lastCheckedAt: z.string().datetime({ offset: true }).nullable(),
    failureReason: z.string().min(1).max(1000).nullable(),
})
    .strict()
    .superRefine((val, ctx) => {
    // Edge-sync lifecycle invariants.
    if (val.status === 'failed' && val.failureReason === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['failureReason'],
            message: 'failed domains must record why',
        });
    }
    if (val.status !== 'failed' && val.failureReason !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['failureReason'],
            message: 'failureReason is only allowed when status is "failed"',
        });
    }
    // Every in-flight or active state must be reconcilable against Edge —
    // only `failed` may lack a claim (claim creation itself can fail).
    if (val.status !== 'failed' && val.edgeClaimId === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['edgeClaimId'],
            message: `domains in state "${val.status}" must reference their Edge claim`,
        });
    }
});
export const statusPageBrandingSchema = z
    .object({
    logoUrl: z.string().url().optional(),
    primaryColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'must be a #rrggbb hex color')
        .optional(),
    headerText: z.string().max(200).optional(),
    supportUrl: z.string().url().optional(),
    supportEmail: z.string().email().optional(),
})
    .strict();
export const statusPageInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    /** Subdomain under {slug}.status.detect.meshtek.io (zero-touch default). */
    slug: slugSchema,
    /** Status pages are per-client (PRD §2.7 — the Guru's proof-of-value
     *  for a specific customer). Required, not optional. */
    clientId: idSchema,
    enabled: z.boolean().default(true),
    components: componentsArraySchema,
    branding: statusPageBrandingSchema.optional(),
    /** Days of uptime history rendered (30/60/90). */
    uptimeDisplayDays: z.union([z.literal(30), z.literal(60), z.literal(90)]).default(90),
    /** Requested custom domain (activation is handled via Edge). */
    customDomain: hostnameSchema.optional(),
})
    .strict();
/** Updates may clear nullable-at-rest fields with an explicit null.
 *  Clearing customDomain triggers Edge domain-claim teardown server-side.
 *  clientId may be changed but never cleared (pages are per-client). */
export const statusPageUpdateSchema = statusPageInputSchema
    .partial()
    .extend({
    branding: statusPageBrandingSchema.nullable().optional(),
    customDomain: hostnameSchema.nullable().optional(),
})
    .strict();
export const statusPageSchema = z
    .object({
    name: z.string().min(1).max(200),
    slug: slugSchema,
    /** Per-client, never null (PRD §2.7). */
    clientId: idSchema,
    enabled: z.boolean(),
    components: componentsArraySchema,
    branding: statusPageBrandingSchema.nullable(),
    uptimeDisplayDays: z.union([z.literal(30), z.literal(60), z.literal(90)]),
    customDomain: statusPageCustomDomainSchema.nullable(),
})
    .extend(baseEntityFields)
    .strict();
//# sourceMappingURL=dashboard.schema.js.map